import { useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { DataItem, connect, navigateTo } from 'scrivito'

import { TicketActionMenu } from './TicketActionMenu'
import { Badge } from '@/Atomic/Atoms/Badge'
import { DataItemLink } from '@/Atomic/Atoms/DataItemLink'
import { Icon } from '@/Atomic/Atoms/Icon'
import { SublistItem } from '@/Atomic/Templates/Sublist'
import { ConfirmationModal } from '@/Components/shared/modals/ConfirmationModal'
import { errorToast } from '@/Components/Toast'
import { DataClassName } from '@/Data/const'
import { useDoubleClick } from '@/hooks/useDoubleClick'
import { useModalHandler } from '@/hooks/useModalHandler'
import { getEnumDisplayValue } from '@/utils/CRUD/read/enum/getDisplayValue'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'
import { formatDateTime } from '@/utils/formatDateTime'
import { openDataItemInNewTab } from '@/utils/openDataItemInNewTab'
import { reportError } from '@/utils/reportError'
import { getDeleteModalSubtitle } from '@/Widgets/DXPDetails/utils/deleteModalSubtitle'
import { getDeleteModalTitle } from '@/Widgets/DXPDetails/utils/deleteModalTitle'

interface TicketSublistItemProps {
  ticket: DataItem
  onOpenDrawer: (ticket: DataItem, startInEditMode?: boolean) => void
  onCreateActivity: (ticket: DataItem) => void
}

export const TicketSublistItem: React.FC<TicketSublistItemProps> = connect(
  ({ ticket, onOpenDrawer, onCreateActivity }) => {
    const { t } = useTranslation()
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [menuTrigger, setMenuTrigger] = useState<boolean>()
    const {
      isOpen: isDeleteModalOpen,
      openModal: openDeleteModal,
      closeModal: closeDeleteModal,
    } = useModalHandler()
    const { schemaAttributes } = getSchemaAttributes(
      getSchemaName(DataClassName.TICKET),
    )

    const title =
      (ticket.get('displayTitle') as string) ||
      (ticket.get('keyword') as string) ||
      ''
    const number = (ticket.get('number') as string) || ''
    const description = (ticket.get('description') as string) || ''
    const dueAt = ticket.get('dueAt')
    const type = ticket.get('type') as string | undefined
    const typeLabel = type
      ? getEnumDisplayValue(schemaAttributes?.type?.enumValues, type) || type
      : ''

    const deleteTicket = async () => {
      try {
        await ticket.delete()
        closeDeleteModal()
      } catch (error) {
        errorToast(error as Error)
        reportError(error)
      }
    }

    const handleCardClick = useDoubleClick<HTMLAnchorElement>(
      () => onOpenDrawer(ticket),
      () => openDataItemInNewTab(ticket),
    )

    const menuItems = [
      {
        icon: 'info-circle',
        label: t('common.seeDetails'),
        onClick: () => onOpenDrawer(ticket),
      },
      {
        icon: 'edit',
        label: t('common.edit'),
        onClick: () => onOpenDrawer(ticket, true),
      },
      {
        icon: 'ticket',
        label: t('common.goToDetailsPage.ticket'),
        onClick: () => navigateTo(ticket),
      },
      {
        icon: 'activity',
        label: t('components.activity.createActivity'),
        onClick: () => onCreateActivity(ticket),
      },
      {
        icon: 'bin',
        label: t('common.delete'),
        onClick: openDeleteModal,
        type: 'danger' as const,
      },
    ]

    return (
      <>
        <SublistItem
          className={classNames('ticket-sublist-item', {
            'ticket-sublist-item--menu-open': isMenuOpen,
          })}
        >
          <DataItemLink
            className="ticket-sublist-item__main"
            dataItem={ticket}
            onClick={(event) => {
              if (
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey ||
                event.button !== 0
              ) {
                return
              }

              event.preventDefault()
              handleCardClick(event)
            }}
            onContextMenu={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setMenuTrigger((currentTrigger) => !currentTrigger)
            }}
          >
            <div className="ticket-sublist-item__icon" aria-hidden="true">
              <Icon icon="ticket" size="md" />
            </div>

            <div className="ticket-sublist-item__content">
              <div className="ticket-sublist-item__title-row">
                <span className="ticket-sublist-item__title">{title}</span>
                <div className="ticket-sublist-item__badges">
                  {dueAt ? (
                    <Badge
                      value={formatDateTime(dueAt, 'MMM d, HH:mm')}
                      variant="info"
                    />
                  ) : null}
                  {typeLabel ? <Badge value={typeLabel} /> : null}
                </div>
              </div>
              {number ? (
                <span className="ticket-sublist-item__number">{number}</span>
              ) : null}
              {description ? (
                <span className="ticket-sublist-item__description">
                  {description}
                </span>
              ) : null}
            </div>
          </DataItemLink>

          <div
            className="ticket-sublist-item__actions"
            onClick={(event) => event.stopPropagation()}
          >
            <TicketActionMenu
              menuItems={menuItems}
              onOpenChange={setIsMenuOpen}
              trigger={menuTrigger}
            />
          </div>
        </SublistItem>
        <ConfirmationModal
          variant="danger"
          isOpen={isDeleteModalOpen}
          close={closeDeleteModal}
          confirm={deleteTicket}
          title={t(getDeleteModalTitle(DataClassName.TICKET))}
          subtitle={t(getDeleteModalSubtitle(DataClassName.TICKET))}
        />
      </>
    )
  },
)
